import { Box, Divider, Link, Popover, Stack, Typography } from "@mui/material"
import React from "react"
import CustomList from "../../components/CustomList"
import { useLessons } from "../../redux/lessonsSlice"
import { useSelector } from "react-redux"
import { useSubjects } from "../../redux/dicts/hooks"
import useStudentLessonActionHandlers from "./hooks/useStudentLessonActionHandlers"
import { useTasks } from "../../redux/taskSlice"
import StudentTaskForm from "./components/StudentTaskForm"
import { useFilesForTask } from "../../redux/filesSlice"
import { useLessonMaterial } from "../../redux/lessonMaterialSlice"
import Spinner from './../../components/Spinner';
import useLocalStorage from "use-local-storage"
import useMenu from "../../hooks/useMenu"
import LessonFilters from "../LessonLogbook/components/LessonFilters"
import { FilterList } from "@mui/icons-material"

const StudentLessonsPage = ({ canEdit }) => {

    const userInfo = useSelector(x => x.user.userInfo)

    const role = React.useMemo(() => !userInfo ? null : userInfo?.role?.name === "родитель" ? "parent" : "student", [userInfo])

    const [filters, setFilters] = useLocalStorage('eschool_sl_filters', {
        subject: null,
        dateBefore: "",
        dateAfter: "",
        timeBefore: "",
        timeAfter: "",
    })

    const { subjectOptions, subjectLookups, subjectsIsLoading } = useSubjects()

    const { lessons, currentLesson, lessonsIsLoading } = useLessons(
        userInfo?.role.id, role,
        null, filters.subject,
        filters.dateBefore, filters.dateAfter,
        filters.timeBefore, filters.timeAfter
    )

    const { tasks, tasksIsLoading } = useTasks(currentLesson, role, userInfo?.role?.id)

    const { lessonMaterial, lessonMaterialIsLoading } = useLessonMaterial(currentLesson)

    const rightSubpageIsLoading = tasksIsLoading || lessonMaterialIsLoading

    const {
        acceptedFiles,
        savedFiles, handleAddFiles, handleRemoveAcceptedFile, handleResetSavedFiles,
    } = useFilesForTask()

    const { handleSetCurrentLesson, handleSaveTask } = useStudentLessonActionHandlers(savedFiles, handleResetSavedFiles)

    const { anchorEl, open, handleDefaultMenuClick, handleMenuClose } = useMenu()


    return <Box sx={{ minWidth: 1050, width: "100%", height: "100%", display: "flex", overflowX: "hidden" }}>

        <Popover open={open} onClose={handleMenuClose} anchorEl={anchorEl}>
            <LessonFilters
                initialValues={filters}
                onSubmit={setFilters}
                subjectOptions={subjectOptions}
                subjectLookups={subjectLookups}
                onClose={handleMenuClose}
                showGroupsFilter={false}
            />
        </Popover>

        <Box sx={{ width: 400, height: "100%" }}>
            <CustomList
                data={lessons}
                current={currentLesson}
                onSelect={handleSetCurrentLesson}
                isLoading={lessonsIsLoading || subjectsIsLoading}
                width={400}
                height="100%"
                primaryValueGetter={x => subjectLookups[x.subject]?.name ?? ""}
                secondaryValueGetter={x => new Date(x.date).toLocaleString().slice(0, 17)}
                freeActions={[{
                    icon: FilterList,
                    onClick: handleDefaultMenuClick,
                }]}
            />
        </Box>

        <Divider orientation="vertical" />

        {currentLesson && <Box sx={{
            width: "calc(100% - 401px)",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            overflowY: "auto",
        }}>
            {rightSubpageIsLoading ? <Spinner height="100%" /> : <Box
                sx={{
                    p: 4,
                    minWidth: 600,
                    maxWidth: 700,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                {lessonMaterial?.id && <Stack direction="column" width="100%" spacing={2} mb={4}>

                    <Typography variant="h5" align="center">Материалы к занятию</Typography>

                    <Typography variant="h6">
                        {lessonMaterial?.name}
                    </Typography>

                    <Typography alignSelf="start" textAlign="justify">
                        {lessonMaterial.comment}
                    </Typography>

                    {lessonMaterial.files.length > 0 && <>
                        <Divider />
                        <Typography>
                            Прикрепленные файлы
                        </Typography>
                        <Stack direction="column">
                            {lessonMaterial.files.map(file => <Link key={file.id} href={file.link}>{file.name}</Link>)}
                        </Stack>
                    </>}
                </Stack>}

                <Typography variant="h5">Задания к занятию</Typography>

                <Divider />

                {tasks.map(task => <React.Fragment key={task.id}>

                    {(role === "parent" && userInfo?.role?.students?.length > 1) && <Typography variant="h6" sx={{ mt: 2 }}>
                        {userInfo?.role?.students.find(el => el.id === task.student)?.full_name}
                    </Typography>}

                    <StudentTaskForm
                        canEdit={canEdit}
                        data={task}
                        acceptedFiles={acceptedFiles?.[task?.id] ?? []}
                        onAddAcceptedFiles={handleAddFiles}
                        onRemoveAcceptedFile={handleRemoveAcceptedFile}
                        onSave={handleSaveTask}
                    />
                </React.Fragment>)}
            </Box>}
        </Box>}
    </Box>
}

export default StudentLessonsPage